import { HStack, Box, Text } from '@chakra-ui/react';
import { useColorModeValue } from './ColorMode';

/**
 * @useFor a status indicator on a row or card — colored dot + label.
 *
 * Examples in-tree: People list `Active` / `Inactive` column;
 * onboarding job status (`pending` / `processing` / `completed` /
 * `failed`); deploy badges in admin dashboards.
 *
 * The `status` enum is canonical — pick the closest semantic value
 * rather than inventing a new one. If the status you need genuinely
 * doesn't fit (e.g. a custom workflow state), extend `StatusType`
 * here rather than rolling a parallel badge component in your app.
 *
 * **Do NOT use** for:
 *   - **Generic colored pills** that aren't status (use `Badge`
 *     directly from `@chakra-ui/react` or a domain-specific component).
 *   - **Tag-style chips** for taxonomy / category labels.
 *   - **Counter / numeric badges** — use `Badge` with `variant`.
 *
 * See `libs/themes/aegix/CONVENTIONS.md` for the full pattern map.
 */
export type StatusType = 'active' | 'inactive' | 'pending' | 'processing' | 'queued' | 'draft' | 'published' | 'archived' | 'info' | 'default' | 'warning' | 'error' | 'completed' | 'failed' | 'success';

type StatusBadgeSize = 'sm' | 'md' | 'lg';

interface StatusBadgeProps {
  status: StatusType;
  label?: string;
  /** Controls dot diameter and label font size. Defaults to 'sm'. */
  size?: StatusBadgeSize;
}

interface StatusConfig {
  color: string;
  darkColor: string;
  dotColor: string;
  darkDotColor: string;
  label: string;
}

const statusConfig: Record<StatusType, StatusConfig> = {
  active: {
    color: 'green.600',
    darkColor: 'green.400',
    dotColor: 'green.500',
    darkDotColor: 'green.400',
    label: 'Active',
  },
  inactive: {
    color: 'red.600',
    darkColor: 'red.300',
    dotColor: 'red.500',
    darkDotColor: 'red.300',
    label: 'Inactive',
  },
  pending: {
    color: 'yellow.600',
    darkColor: 'yellow.500',
    dotColor: 'yellow.500',
    darkDotColor: 'yellow.400',
    label: 'Pending',
  },
  draft: {
    color: 'gray.600',
    darkColor: 'gray.400',
    dotColor: 'gray.500',
    darkDotColor: 'gray.400',
    label: 'Draft',
  },
  published: {
    color: 'blue.600',
    darkColor: 'blue.300',
    dotColor: 'blue.500',
    darkDotColor: 'blue.300',
    label: 'Published',
  },
  archived: {
    color: 'gray.600',
    darkColor: 'gray.400',
    dotColor: 'gray.400',
    darkDotColor: 'gray.400',
    label: 'Archived',
  },
  info: {
    color: 'blue.600',
    darkColor: 'blue.300',
    dotColor: 'blue.500',
    darkDotColor: 'blue.300',
    label: 'Info',
  },
  default: {
    color: 'gray.600',
    darkColor: 'gray.400',
    dotColor: 'gray.500',
    darkDotColor: 'gray.400',
    label: 'Default',
  },
  warning: {
    color: 'orange.600',
    darkColor: 'orange.400',
    dotColor: 'orange.500',
    darkDotColor: 'orange.400',
    label: 'Warning',
  },
  error: {
    color: 'red.600',
    darkColor: 'red.300',
    dotColor: 'red.500',
    darkDotColor: 'red.300',
    label: 'Error',
  },
  processing: {
    color: 'blue.600',
    darkColor: 'blue.300',
    dotColor: 'blue.500',
    darkDotColor: 'blue.300',
    label: 'Processing',
  },
  queued: {
    color: 'yellow.600',
    darkColor: 'yellow.500',
    dotColor: 'yellow.500',
    darkDotColor: 'yellow.400',
    label: 'Queued',
  },
  completed: {
    color: 'green.600',
    darkColor: 'green.400',
    dotColor: 'green.500',
    darkDotColor: 'green.400',
    label: 'Completed',
  },
  failed: {
    color: 'red.600',
    darkColor: 'red.300',
    dotColor: 'red.500',
    darkDotColor: 'red.300',
    label: 'Failed',
  },
  success: {
    color: 'green.600',
    darkColor: 'green.400',
    dotColor: 'green.500',
    darkDotColor: 'green.400',
    label: 'Success',
  },
};

const dotDimByLabel: Record<StatusBadgeSize, number> = {
  sm: 2,
  md: 2.5,
  lg: 3,
};
const fontSizeByLabel: Record<StatusBadgeSize, string> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
};

export function StatusBadge({ status, label, size = 'sm' }: StatusBadgeProps) {
  const config = statusConfig[status] ?? statusConfig.default;
  const displayLabel = label || config.label;
  const textColor = useColorModeValue(config.color, config.darkColor);
  const dotBg = useColorModeValue(config.dotColor, config.darkDotColor);

  const dotDim = dotDimByLabel[size];
  const fontSize = fontSizeByLabel[size];

  return (
    <HStack gap={2} role="status">
      <Box
        w={dotDim}
        h={dotDim}
        borderRadius="full"
        bg={dotBg}
        aria-hidden="true"
      />
      <Text fontSize={fontSize} fontWeight="medium" color={textColor}>
        {displayLabel}
      </Text>
    </HStack>
  );
}
